import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import {
  MessageSquare, Zap, BarChart2, Bot, Package, TrendingUp,
  CheckCircle, Clock, DollarSign, ArrowUpRight, Users,
  Search, Lightbulb, Truck, Target, UserCheck, ShoppingCart, Headphones
} from 'lucide-react';

const services = [
  {
    id: 'whatsapp',
    icon: MessageSquare,
    color: 'bg-green-500',
    lightColor: 'bg-green-50',
    borderColor: 'border-green-200',
    textColor: 'text-green-700',
    titleKey: 'svc.whatsapp.title',
    subtitleKey: 'svc.whatsapp.subtitle',
    descKey: 'svc.whatsapp.desc',
    benefitsKeys: ['svc.whatsapp.b1','svc.whatsapp.b2','svc.whatsapp.b3','svc.whatsapp.b4'],
    benefitIcons: [Zap, Clock, DollarSign, Users],
    typesKey: 'svc.whatsapp.types',
    types: [
      { icon: Target,       labelKey: 'svc.whatsapp.t1.label', descKey: 'svc.whatsapp.t1.desc' },
      { icon: ShoppingCart, labelKey: 'svc.whatsapp.t2.label', descKey: 'svc.whatsapp.t2.desc' },
      { icon: Headphones,   labelKey: 'svc.whatsapp.t3.label', descKey: 'svc.whatsapp.t3.desc' },
      { icon: UserCheck,    labelKey: 'svc.whatsapp.t4.label', descKey: 'svc.whatsapp.t4.desc' },
    ],
  },
  {
    id: 'business-automation',
    icon: Zap,
    color: 'bg-blue-500',
    lightColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    textColor: 'text-blue-700',
    titleKey: 'svc.business.title',
    subtitleKey: 'svc.business.subtitle',
    descKey: 'svc.business.desc',
    benefitsKeys: ['svc.business.b1','svc.business.b2','svc.business.b3','svc.business.b4'],
    benefitIcons: [Clock, CheckCircle, DollarSign, ArrowUpRight],
    types: [],
  },
  {
    id: 'data-analysis',
    icon: BarChart2,
    color: 'bg-violet-500',
    lightColor: 'bg-violet-50',
    borderColor: 'border-violet-200',
    textColor: 'text-violet-700',
    titleKey: 'svc.data.title',
    subtitleKey: 'svc.data.subtitle',
    descKey: 'svc.data.desc',
    benefitsKeys: ['svc.data.b1','svc.data.b2','svc.data.b3','svc.data.b4'],
    benefitIcons: [BarChart2, Search, Lightbulb, TrendingUp],
    types: [],
  },
  {
    id: 'chatbots',
    icon: Bot,
    color: 'bg-cyan-500',
    lightColor: 'bg-cyan-50',
    borderColor: 'border-cyan-200',
    textColor: 'text-cyan-700',
    titleKey: 'svc.chatbots.title',
    subtitleKey: 'svc.chatbots.subtitle',
    descKey: 'svc.chatbots.desc',
    benefitsKeys: ['svc.chatbots.b1','svc.chatbots.b2','svc.chatbots.b3','svc.chatbots.b4'],
    benefitIcons: [Clock, MessageSquare, Zap, UserCheck],
    types: [],
  },
  {
    id: 'logistics',
    icon: Truck,
    color: 'bg-orange-500',
    lightColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
    textColor: 'text-orange-700',
    titleKey: 'svc.logistics.title',
    subtitleKey: 'svc.logistics.subtitle',
    descKey: 'svc.logistics.desc',
    benefitsKeys: ['svc.logistics.b1','svc.logistics.b2','svc.logistics.b3','svc.logistics.b4'],
    benefitIcons: [DollarSign, Search, UserCheck, ArrowUpRight],
    types: [],
  },
  {
    id: 'marketing',
    icon: TrendingUp,
    color: 'bg-pink-500',
    lightColor: 'bg-pink-50',
    borderColor: 'border-pink-200',
    textColor: 'text-pink-700',
    titleKey: 'svc.marketing.title',
    subtitleKey: 'svc.marketing.subtitle',
    descKey: 'svc.marketing.desc',
    benefitsKeys: ['svc.marketing.b1','svc.marketing.b2','svc.marketing.b3','svc.marketing.b4'],
    benefitIcons: [Target, UserCheck, BarChart2, ArrowUpRight],
    types: [],
  },
];

export function ServicesDetailSection() {
  const { t } = useLanguage();
  const [active, setActive] = useState(0);
  const svc = services[active];

  return (
    <section id="services-detail" className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="text-center mb-14">
          <span className="inline-block bg-primary/10 text-primary font-semibold text-sm px-4 py-1.5 rounded-full uppercase tracking-wide mb-4">
            {t('svc.badge')}
          </span>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('svc.title')}</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">{t('svc.subtitle')}</p>
        </div>

        {/* Tab navigation */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {services.map((s, i) => {
            const Icon = s.icon;
            return (
              <button
                key={s.id}
                onClick={() => setActive(i)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 border
                  ${active === i
                    ? `${s.color} text-white border-transparent shadow-md scale-105`
                    : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:shadow-sm'
                  }`}
              >
                <Icon className="w-4 h-4" />
                {t(s.titleKey).split(' ').slice(0, 3).join(' ')}
              </button>
            );
          })}
        </div>

        {/* Active service content */}
        <div className={`bg-white border ${svc.borderColor} rounded-3xl overflow-hidden shadow-sm`}>

          {/* Service hero */}
          <div className={`${svc.lightColor} px-8 py-10 border-b ${svc.borderColor}`}>
            <div className="flex items-start gap-5">
              <div className={`${svc.color} w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-md`}>
                <svc.icon className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-1">{t(svc.titleKey)}</h3>
                <p className={`font-semibold mb-3 ${svc.textColor}`}>{t(svc.subtitleKey)}</p>
                <p className="text-gray-600 leading-relaxed max-w-3xl">{t(svc.descKey)}</p>
              </div>
            </div>
          </div>

          {/* Benefits */}
          <div className="px-8 py-8">
            <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-widest mb-6">
              {t('svc.benefitsTitle')}
            </h4>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
              {svc.benefitsKeys.map((bk, j) => {
                const BIcon = svc.benefitIcons[j];
                return (
                  <div key={j} className="flex items-start gap-3">
                    <div className={`${svc.lightColor} w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0`}>
                      <BIcon className={`w-4 h-4 ${svc.textColor}`} />
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed">{t(bk)}</p>
                  </div>
                );
              })}
            </div>

            {/* WhatsApp types */}
            {svc.types && svc.types.length > 0 && (
              <>
                <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-widest mb-5">
                  {t('svc.whatsapp.types')}
                </h4>
                <div className="grid sm:grid-cols-2 gap-4">
                  {svc.types.map((tp, k) => {
                    const TIcon = tp.icon;
                    return (
                      <div key={k} className={`${svc.lightColor} border ${svc.borderColor} rounded-xl p-4 flex items-start gap-3`}>
                        <TIcon className={`w-5 h-5 ${svc.textColor} flex-shrink-0 mt-0.5`} />
                        <div>
                          <p className="font-semibold text-gray-900 text-sm mb-1">{t(tp.labelKey)}</p>
                          <p className="text-gray-600 text-xs leading-relaxed">{t(tp.descKey)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {/* CTA */}
          <div className="px-8 pb-8">
            <a
              href="#get-started"
              className={`inline-flex items-center gap-2 ${svc.color} hover:opacity-90 text-white font-semibold px-6 py-3 rounded-xl transition-all duration-200 shadow-sm`}
            >
              {t('svc.cta')} →
            </a>
          </div>
        </div>

      </div>
    </section>
  );
}